import React from "react";
import { Box, ListItemButton, Typography, alpha, useTheme } from "@mui/material";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import { getTeamSpaceLabel } from "../../components/TeamChat/teamSpaceLabel";
import TeamPresenceAvatar from "../../components/TeamPresenceAvatar";
import { getInitials } from "../../utils/utils";

function DmAvatar({ space, selected, label }) {
  const theme = useTheme();

  if (space.dmPeerType === "agent") {
    return (
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: selected ? "secondary.main" : alpha(theme.palette.secondary.main, 0.15),
          color: selected ? "secondary.contrastText" : "secondary.main",
          flexShrink: 0,
        }}
      >
        <SmartToyOutlinedIcon sx={{ fontSize: 18 }} />
      </Box>
    );
  }

  if (space.dmPeerType === "chatbot") {
    return (
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: selected ? "info.main" : alpha(theme.palette.info.main, 0.15),
          color: selected ? "info.contrastText" : "info.main",
          flexShrink: 0,
        }}
      >
        <SupportAgentOutlinedIcon sx={{ fontSize: 18 }} />
      </Box>
    );
  }

  const peerId = space.dmPeerUserId;
  if (peerId) {
    return (
      <TeamPresenceAvatar
        userId={peerId}
        src={space.dmPeerAvatarUrl}
        name={label}
        size={32}
        showDot={false}
      />
    );
  }

  return (
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 13,
        fontWeight: 600,
        bgcolor: selected ? "primary.main" : alpha(theme.palette.primary.main, 0.15),
        color: selected ? "primary.contrastText" : "primary.main",
        flexShrink: 0,
      }}
    >
      {getInitials(label)}
    </Box>
  );
}

const PortalTeamInboxItem = ({ item, selected, onSelect, hideWorkspaceLabel = false }) => {
  const theme = useTheme();
  const label = getTeamSpaceLabel(item);
  const unread = Number(item.unreadCount) || 0;

  return (
    <ListItemButton
      selected={selected}
      onClick={onSelect}
      sx={{
        mx: 1,
        mb: 0.25,
        borderRadius: 1.5,
        py: 1,
        color: "text.primary",
        "&.Mui-selected": {
          bgcolor: alpha(theme.palette.primary.main, 0.1),
          "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.14) },
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, width: "100%", minWidth: 0 }}>
        <DmAvatar space={item} selected={selected} label={label} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="body2" color="text.primary" fontWeight={unread > 0 || selected ? 700 : 500} noWrap sx={{ flex: 1 }}>
              {label}
            </Typography>
            {unread > 0 ? (
              <Box
                sx={{
                  minWidth: 20,
                  height: 20,
                  px: 0.75,
                  borderRadius: 10,
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {unread > 9 ? "9+" : unread}
              </Box>
            ) : null}
          </Box>
          {item.lastMessagePreview || !hideWorkspaceLabel ? (
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {!hideWorkspaceLabel && (item.workspaceLabel || item.workspaceSlug)
                ? `${item.workspaceLabel || item.workspaceSlug}${item.lastMessagePreview ? ` · ${item.lastMessagePreview}` : ""}`
                : item.lastMessagePreview || ""}
            </Typography>
          ) : null}
        </Box>
      </Box>
    </ListItemButton>
  );
};

export default PortalTeamInboxItem;
